import React, { useEffect, useMemo, useState } from 'react';
import { db, storage } from '../firebase';
import { deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const CONFIG_DOC = { collection: 'site-config', id: 'delivery-files' };

const DEFAULTS = {
  industrial: {
    titleKo: '산업용 납품실적',
    titleEn: 'Industrial Delivery Performance',
    filename: '산업용_납품실적_2021_2024.xlsx',
  },
  compact: {
    titleKo: '컴팩트 유닛 납품실적',
    titleEn: 'Compact Unit Delivery Performance',
    filename: 'Compact_Unit_납품실적_2023_2024.xlsx',
  },
};

function buildStoragePath(key, file) {
  const safeName = (file?.name || 'file').replace(/[^\w.\-() ]/g, '_');
  return `delivery-files/${key}/${Date.now()}_${safeName}`;
}

export default function DeliveryFilesManagement() {
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [config, setConfig] = useState(null);
  const [message, setMessage] = useState('');

  // 요구 UX:
  // - "생성" 버튼 → 모달 열림 → 입력 + 파일 업로드 후 생성
  // - 기존 카드 "수정" 버튼 → 모달 열림 → 정보 수정 + (선택) 파일 교체
  // - 삭제 버튼
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create | edit | edit-default
  const [editingDefaultKey, setEditingDefaultKey] = useState(''); // industrial | compact
  const [draft, setDraft] = useState({
    id: '',
    titleKo: '',
    titleEn: '',
    tagKo: '',
    tagEn: '',
  });
  const [draftFile, setDraftFile] = useState(null); // File

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const snap = await getDoc(doc(db, CONFIG_DOC.collection, CONFIG_DOC.id));
      setConfig(snap.exists() ? snap.data() : {});
    } catch (e) {
      console.error('납품실적 설정 로드 실패:', e);
      setMessage('❌ 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const customItems = useMemo(() => {
    const items = Array.isArray(config?.customItems) ? config.customItems : [];
    // stable ordering by createdAt (fallback) then title
    return [...items].sort((a, b) => {
      const at = (a?.createdAt || '').toString();
      const bt = (b?.createdAt || '').toString();
      if (at && bt && at !== bt) return at.localeCompare(bt);
      return (a?.titleKo || a?.titleEn || '').localeCompare(
        b?.titleKo || b?.titleEn || ''
      );
    });
  }, [config]);

  const openEditDefaultModal = (key) => {
    setModalMode('edit-default');
    setEditingDefaultKey(key);
    setDraft({ id: '', titleKo: '', titleEn: '', tagKo: '', tagEn: '' });
    setDraftFile(null);
    setIsModalOpen(true);
  };

  const upsertCustomItems = async (nextItems) => {
    const next = {
      ...(config || {}),
      customItems: nextItems,
      updatedAt: new Date().toISOString(),
    };
    await setDoc(doc(db, CONFIG_DOC.collection, CONFIG_DOC.id), next, {
      merge: true,
    });
    setConfig(next);
  };

  const openCreateModal = () => {
    setModalMode('create');
    setDraft({ id: '', titleKo: '', titleEn: '', tagKo: '', tagEn: '' });
    setDraftFile(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setDraft({
      id: item.id,
      titleKo: item.titleKo || '',
      titleEn: item.titleEn || '',
      tagKo: item.tagKo || '',
      tagEn: item.tagEn || '',
    });
    setDraftFile(null); // 파일 교체는 선택
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (savingKey) return;
    setIsModalOpen(false);
    setDraftFile(null);
    setEditingDefaultKey('');
  };

  const uploadExcel = async ({ key, file }) => {
    const storagePath = buildStoragePath(key, file);
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, {
      contentType:
        file.type ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = await getDownloadURL(storageRef);
    return { url, storagePath };
  };

  const submitModal = async () => {
    if (modalMode === 'edit-default') {
      if (!editingDefaultKey) return;
      if (!draftFile) {
        setMessage('❌ 엑셀(.xlsx) 파일을 선택해주세요.');
        return;
      }

      setSavingKey(`default:${editingDefaultKey}`);
      setMessage('');
      try {
        const { url, storagePath } = await uploadExcel({
          key: editingDefaultKey,
          file: draftFile,
        });

        const next = {
          ...(config || {}),
          [editingDefaultKey]: {
            ...(config?.[editingDefaultKey] || {}),
            ...DEFAULTS[editingDefaultKey],
            fileUrl: url,
            fileName: draftFile.name,
            storagePath,
            updatedAt: new Date().toISOString(),
          },
        };

        await setDoc(doc(db, CONFIG_DOC.collection, CONFIG_DOC.id), next, {
          merge: true,
        });

        setConfig(next);
        setMessage('✅ 파일 교체 완료!');
        closeModal();
      } catch (e) {
        console.error('납품실적(기본 항목 수정) 실패:', e);
        setMessage('❌ 파일 교체에 실패했습니다.');
      } finally {
        setSavingKey('');
      }
      return;
    }

    if (!draft.titleKo.trim() && !draft.titleEn.trim()) {
      setMessage('❌ 제목(국문/영문) 중 하나는 입력해주세요.');
      return;
    }

    if (modalMode === 'create' && !draftFile) {
      setMessage('❌ 엑셀(.xlsx) 파일을 선택해주세요.');
      return;
    }

    setMessage('');
    const now = new Date().toISOString();

    if (modalMode === 'create') {
      setSavingKey('custom:add');
      try {
        const id = `custom_${Date.now()}`;
        const { url, storagePath } = await uploadExcel({
          key: id,
          file: draftFile,
        });

        const nextItems = [
          ...customItems,
          {
            id,
            titleKo: draft.titleKo.trim(),
            titleEn: draft.titleEn.trim(),
            tagKo: draft.tagKo.trim(),
            tagEn: draft.tagEn.trim(),
            fileUrl: url,
            fileName: draftFile.name,
            storagePath,
            createdAt: now,
            updatedAt: now,
          },
        ];

        await upsertCustomItems(nextItems);
        setMessage('✅ 생성 완료!');
        closeModal();
      } catch (e) {
        console.error('납품실적(생성) 실패:', e);
        setMessage('❌ 생성에 실패했습니다.');
      } finally {
        setSavingKey('');
      }
      return;
    }

    // edit
    setSavingKey(`custom:edit:${draft.id}`);
    try {
      const nextItems = await Promise.all(
        customItems.map(async (it) => {
          if (it.id !== draft.id) return it;

          let nextFile = {};
          if (draftFile) {
            const { url, storagePath } = await uploadExcel({
              key: it.id,
              file: draftFile,
            });
            nextFile = {
              fileUrl: url,
              fileName: draftFile.name,
              storagePath,
            };
          }

          return {
            ...it,
            titleKo: draft.titleKo.trim(),
            titleEn: draft.titleEn.trim(),
            tagKo: draft.tagKo.trim(),
            tagEn: draft.tagEn.trim(),
            ...nextFile,
            updatedAt: now,
          };
        })
      );

      await upsertCustomItems(nextItems);
      setMessage('✅ 수정 완료!');
      closeModal();
    } catch (e) {
      console.error('납품실적(수정) 실패:', e);
      setMessage('❌ 수정에 실패했습니다.');
    } finally {
      setSavingKey('');
    }
  };

  const deleteCustom = async (id) => {
    if (!window.confirm('정말로 이 엑셀 항목을 삭제하시겠습니까?')) return;
    setSavingKey(`custom:delete:${id}`);
    setMessage('');
    try {
      const nextItems = customItems.filter((it) => it.id !== id);
      await upsertCustomItems(nextItems);
      setMessage('✅ 삭제 완료!');
    } catch (e) {
      console.error('납품실적(삭제) 실패:', e);
      setMessage('❌ 삭제에 실패했습니다.');
    } finally {
      setSavingKey('');
    }
  };

  const resetDefaultToPublic = async (key) => {
    if (!window.confirm('기본 파일 사용으로 되돌릴까요?')) return;
    setSavingKey(`default:reset:${key}`);
    setMessage('');
    try {
      const refDoc = doc(db, CONFIG_DOC.collection, CONFIG_DOC.id);
      await updateDoc(refDoc, {
        [`${key}.fileUrl`]: deleteField(),
        [`${key}.fileName`]: deleteField(),
        [`${key}.storagePath`]: deleteField(),
        [`${key}.updatedAt`]: deleteField(),
      });

      // 로컬 상태도 갱신
      const next = { ...(config || {}) };
      if (next[key]) {
        delete next[key].fileUrl;
        delete next[key].fileName;
        delete next[key].storagePath;
        delete next[key].updatedAt;
      }
      setConfig(next);
      setMessage('✅ 기본 파일 사용으로 되돌렸습니다.');
    } catch (e) {
      console.error('기본 파일 리셋 실패:', e);
      setMessage('❌ 되돌리기에 실패했습니다.');
    } finally {
      setSavingKey('');
    }
  };

  const renderRow = (key, label) => {
    const current = { ...DEFAULTS[key], ...(config?.[key] || {}) };
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '1rem',
          marginBottom: '1rem',
        }}
      >
        <h3 style={{ margin: 0, marginBottom: '0.75rem' }}>{label}</h3>
        <div style={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.5 }}>
          <div>
            <strong>기본 파일:</strong> {DEFAULTS[key].filename}
          </div>
          <div>
            <strong>현재 적용:</strong>{' '}
            {current.fileUrl ? (
              <a href={current.fileUrl} target="_blank" rel="noreferrer">
                {current.fileName || '업로드 파일'}
              </a>
            ) : (
              '(기본 파일 사용 중)'
            )}
          </div>
        </div>

        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="btn-secondary"
            onClick={() => openEditDefaultModal(key)}
            disabled={!!savingKey}
            style={{ padding: '0.45rem 0.8rem', fontSize: '0.9rem' }}
          >
            수정
          </button>
          <button
            className="btn-secondary"
            onClick={() => resetDefaultToPublic(key)}
            disabled={!!savingKey || !current.fileUrl}
            style={{ padding: '0.45rem 0.8rem', fontSize: '0.9rem' }}
          >
            기본으로
          </button>
          {savingKey === key && (
            <div style={{ marginLeft: '0.25rem', color: '#666' }}>업로드 중...</div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '1rem' }}>불러오는 중...</div>;
  }

  return (
    <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: 8 }}>
      <h2 style={{ marginTop: 0 }}>납품실적 엑셀 업데이트</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        여기서 업로드한 파일은 납품실적 페이지의 다운로드 버튼에 바로 연결됩니다.
      </p>

      {message && (
        <div style={{ marginBottom: '1rem', fontWeight: 600 }}>{message}</div>
      )}

      {renderRow('industrial', '산업용 납품실적')}
      {renderRow('compact', '컴팩트 유닛 납품실적')}

      <div style={{ marginTop: '2rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
          }}
        >
          <h3 style={{ margin: 0 }}>추가 엑셀 항목</h3>
          <button
            className="btn-primary"
            onClick={openCreateModal}
            style={{ padding: '0.5rem 0.9rem', fontSize: '0.9rem' }}
            disabled={!!savingKey}
          >
            생성
          </button>
        </div>

        {customItems.length === 0 ? (
          <div style={{ color: '#666' }}>추가된 항목이 없습니다.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
            {customItems.map((it) => (
              <div
                key={it.id}
                style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '1rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {it.titleKo || '(제목 없음)'} {it.titleEn ? ` / ${it.titleEn}` : ''}
                    </div>
                    {(it.tagKo || it.tagEn) && (
                      <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                        태그: {it.tagKo || '-'} / {it.tagEn || '-'}
                      </div>
                    )}
                    <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                      파일:{' '}
                      {it.fileUrl ? (
                        <a href={it.fileUrl} target="_blank" rel="noreferrer">
                          {it.fileName || '엑셀'}
                        </a>
                      ) : (
                        '(없음)'
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button
                      className="btn-secondary"
                      onClick={() => openEditModal(it)}
                      style={{ padding: '0.45rem 0.75rem', fontSize: '0.85rem' }}
                      disabled={!!savingKey}
                    >
                      수정
                    </button>
                    <button
                      className="btn-logout"
                      onClick={() => deleteCustom(it.id)}
                      style={{ padding: '0.45rem 0.75rem', fontSize: '0.85rem' }}
                      disabled={!!savingKey}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div
          className="form-modal"
          onClick={closeModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
          }}
        >
          <div
            className="form-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              width: 'min(720px, 92vw)',
              borderRadius: 10,
              padding: '1.25rem',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                paddingBottom: '0.75rem',
                marginBottom: '0.75rem',
              }}
            >
              <h3 style={{ margin: 0 }}>
                {modalMode === 'create'
                  ? '납품실적 엑셀 생성'
                  : modalMode === 'edit-default'
                    ? '납품실적 엑셀 파일 교체'
                    : '납품실적 엑셀 수정'}
              </h3>
              <button
                onClick={closeModal}
                style={{ border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer' }}
                disabled={!!savingKey}
              >
                ×
              </button>
            </div>

            {modalMode !== 'edit-default' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ fontWeight: 600 }}>제목(국문)</label>
                <input
                  value={draft.titleKo}
                  onChange={(e) => setDraft({ ...draft, titleKo: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6 }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600 }}>Title(EN)</label>
                <input
                  value={draft.titleEn}
                  onChange={(e) => setDraft({ ...draft, titleEn: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6 }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600 }}>태그(국문, 선택)</label>
                <input
                  value={draft.tagKo}
                  onChange={(e) => setDraft({ ...draft, tagKo: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6 }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600 }}>Tag(EN, optional)</label>
                <input
                  value={draft.tagEn}
                  onChange={(e) => setDraft({ ...draft, tagEn: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6 }}
                />
              </div>
            </div>
            )}

            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontWeight: 600 }}>
                엑셀 파일(.xlsx){
                  modalMode === 'create' || modalMode === 'edit-default'
                    ? ' (필수)'
                    : ' (교체 시 선택)'
                }
              </label>
              <input
                type="file"
                accept=".xlsx"
                disabled={!!savingKey}
                onChange={(e) => setDraftFile(e.target.files?.[0] || null)}
                style={{ display: 'block', marginTop: '0.4rem' }}
              />
              {draftFile && (
                <div style={{ marginTop: '0.4rem', color: '#666', fontSize: '0.9rem' }}>
                  선택됨: {draftFile.name}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={closeModal} disabled={!!savingKey}>
                취소
              </button>
              <button className="btn-primary" onClick={submitModal} disabled={!!savingKey}>
                {savingKey ? '처리 중...' : modalMode === 'create' ? '생성' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

